import { createSlice } from "@reduxjs/toolkit";
import {
  sliceFirebaseShop,
  sliceFirebaseGroups,
  sliceFirebaseProducts,
} from "../utils/firebase-adapters";
import { minimalDelayedHOC } from "../utils/minimalDelayedHOC";
import {
  setShopDetails,
  setOrderAvailability,
  setTotemData,
} from "./shopSlice";
import { setGroupedProductsWithQuestions } from "./productsSlice";
import { requireProducts } from "../ducks/productsSlice";
import { refreshCachedCosts } from "../ducks/shoppingCartSlice";
import { validateCache } from "../ducks/checkoutSlice";
import { STATE_STEP, API_PALLAS_APPS_URL } from "./consts";
import getFirebaseData from "../utils/firebase-get-data";

export const appStateSlice = createSlice({
  name: "appState",
  initialState: {
    namespace: "",
    showOnlyStatus: false,
    step: STATE_STEP.INITIAL,
    error: null,
    time: 0,
  },
  reducers: {
    setLoading: (state, { payload }) => {
      state.step = STATE_STEP.LOADING;
      state.namespace = payload.namespace;
      state.showOnlyStatus = payload.showOnlyStatus;
    },
    setReady: (state) => {
      state.step = STATE_STEP.READY;
    },
    setError: (state, action) => {
      state.step = STATE_STEP.ERROR;
      state.error = action.payload;
    },
    setTime: (state, { payload }) => {
      state.time = payload.time;
    },
  },
});

const { setLoading, setReady, setError, setTime } = appStateSlice.actions;

const loadShop = async (namespace, dispatch) => {
  // A Cloud Function `ssr` embutia os dados da loja num <script id="jsonEmbedded">
  // e isto lia dali. Saiu: aquilo prendia o HTML por loja a dados que mudam,
  // impedindo qualquer cache de CDN na resposta do SSR. Agora a function só
  // monta metadado (título, og:*, ícone) e os dados vêm daqui, que já era o
  // caminho de fallback.
  const loja = await getFirebaseData(`/${namespace}/loja`);
  const shop = sliceFirebaseShop(loja);
  dispatch(setShopDetails(shop));
};

const loadTotem = async (tableId, dispatch) => {
  const [, id, uuid] = tableId.split("-");
  const response = await fetch(
    `${API_PALLAS_APPS_URL}/validaTotem?id=${id}&uuid=${uuid}`,
  );
  const data = await response.json();
  if (response.ok) {
    dispatch(setTotemData(data));
    return;
  }

  throw new Error(
    `Ocorreu um erro ao validar a URL do Totem. ${data?.error?.message}`,
  );
};

const loadProducts = async (
  namespace,
  isInLoco,
  dispatch,
  getState,
  versao,
) => {
  const orderType = isInLoco ? "mesa" : "entrega";
  const data = await getFirebaseData(`/${namespace}/${orderType}`, { versao });
  const parsed = {
    groups: sliceFirebaseGroups(data.grupos),
    items: sliceFirebaseProducts(data.produtos),
  };
  await dispatch(setGroupedProductsWithQuestions(parsed));
  await dispatch(
    setOrderAvailability({ isAvailableToOrder: data["podePedir"] }),
  );

  const orders = getState().shoppingCart.productOrders.filter(
    (o) => o.commited,
  );
  for (const o of orders) {
    await dispatch(requireProducts(o.productId));
  }

  await dispatch(refreshCachedCosts());
  await dispatch(validateCache());
};

export const fetchAppEssentials =
  (namespace, isInLoco, showOnlyStatus) => async (dispatch, getState) => {
    const calmDispatch = minimalDelayedHOC(dispatch, 1200);

    dispatch(
      setLoading({
        namespace,
        showOnlyStatus,
      }),
    );

    try {
      await loadShop(namespace, dispatch);

      const { tableId } = getState().shop;
      if (tableId && tableId.startsWith("totem-")) {
        await loadTotem(tableId, dispatch);
      }

      if (!showOnlyStatus) {
        await loadProducts(namespace, isInLoco, dispatch, getState);
      }

      calmDispatch(setReady());
    } catch (error) {
      console.error(error);
      const message = typeof error === "string" ? error : error?.message;
      calmDispatch(setError(message));
    }
  };

// --- republicação (autoatendimento) ------------------------------------------
//
// O `publicado/time` muda a cada republicação do cardápio. O App acompanha pelo
// stream do RTDB (utils/watchPublishedTime.js), com polling quando o stream
// cai, e os dois chegam aqui pelo applyAppTime.

// O stream avisa no instante da escrita do `time`, e a ordem em que o backend
// grava os nós da publicação não é garantida por este repositório: a pausa evita
// recarregar o cardápio antes de ele terminar de ser gravado.
export const ATRASO_RECARGA_MS = 2000;
const TENTATIVAS_RECARGA = 3;

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Uma recarga por vez. Republicação que chega no meio entra como pendente, e só
// a mais nova é aplicada depois.
let versaoEmCurso = null;
let versaoPendente = null;

const recarregarPublicado = async (namespace, versao, dispatch, getState) => {
  // a versão vai na leitura: na rota /_dados ela fura o cache do CDN
  const loja = await getFirebaseData(`/${namespace}/loja`, { versao });
  dispatch(setShopDetails(sliceFirebaseShop(loja)));
  await loadProducts(namespace, true, dispatch, getState, versao);
};

const aplicarRepublicacao =
  (namespace, versao) => async (dispatch, getState) => {
    if (versaoEmCurso !== null) {
      if (versao > (versaoPendente || versaoEmCurso)) versaoPendente = versao;
      return;
    }

    let proxima = versao;
    while (proxima !== null) {
      versaoEmCurso = proxima;
      versaoPendente = null;
      for (let tentativa = 1; tentativa <= TENTATIVAS_RECARGA; tentativa++) {
        try {
          await esperar(ATRASO_RECARGA_MS * tentativa);
          await recarregarPublicado(
            namespace,
            versaoEmCurso,
            dispatch,
            getState,
          );
          // O `time` só avança com o cardápio novo na tela. Se as tentativas
          // falharem, o próximo valor (polling ou reconexão do stream) tenta de
          // novo.
          dispatch(setTime({ time: versaoEmCurso }));
          break;
        } catch (error) {
          console.error(error);
        }
      }
      proxima = versaoPendente;
    }
    versaoEmCurso = null;
  };

export const applyAppTime = (namespace, time) => (dispatch, getState) => {
  const lastTime = getState().appState.time;
  if (lastTime === 0) {
    // primeiro valor: o cardápio na tela é o do boot
    dispatch(setTime({ time }));
    return Promise.resolve();
  }
  if (time > lastTime) {
    return dispatch(aplicarRepublicacao(namespace, time));
  }
  return Promise.resolve();
};

export const fetchAppTime = (namespace) => async (dispatch) => {
  const time = await getFirebaseData(`/${namespace}/publicado/time`);
  await dispatch(applyAppTime(namespace, time));
};

export default appStateSlice.reducer;
